import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { MessageRef, Message, MessageType } from '@/entities/chat/model/types';
import { buildStompAuthHeaders, canOpenStompConnection } from '@/shared/lib/stompAuth';
import { ticketedSockJs } from '@/shared/api/wsTicket';

const isTauri = typeof window !== 'undefined' && !!(window as unknown as Record<string, unknown>).__TAURI_INTERNALS__;
// Último recurso aponta para PRODUÇÃO: staging DEVE definir NEXT_PUBLIC_CRM_WS_URL (ou NEXT_PUBLIC_CRM_API_URL).
const defaultCrmWsOrigin = "https://7days-gestaodeobras-be-crm-production.up.railway.app";
const envCrmWsOrigin = process.env.NEXT_PUBLIC_CRM_WS_URL || process.env.NEXT_PUBLIC_CRM_API_URL?.replace(/\/(api|chat)\/?$/, "");
if (!envCrmWsOrigin && process.env.NODE_ENV === 'production') {
  console.warn('[chatWebSocket] NEXT_PUBLIC_CRM_WS_URL ausente; usando fallback de PRODUÇÃO:', defaultCrmWsOrigin);
}
const absoluteWsUrl = `${envCrmWsOrigin || defaultCrmWsOrigin}/api/ws`;

const CRM_WS_ENDPOINT = isTauri && process.env.NODE_ENV === 'production' ? absoluteWsUrl : (process.env.NEXT_PUBLIC_CRM_WS_PATH || '/api/chat/ws');

export type SignalType = 'OFFER' | 'ANSWER' | 'ICE_CANDIDATE' | 'HANG_UP' | 'REJECT' | 'SERVER_MUTE' | 'PLAY_SOUND' | 'PARTICIPANT_LIST' | 'ROOM_JOINED' | 'ROOM_LEFT';

export interface SignalResponse {
  senderId: string;
  type: SignalType;
  payload: Record<string, unknown>;
}

class ChatWebSocketService {
  private client: Client;
  private onConnectCallback?: () => void;
  private onDisconnectCallback?: () => void;
  private subscriptions: Map<string, StompSubscription> = new Map();
  private delayedSubscriptions: Array<{topic: string, action: () => void}> = [];
  private delayedPublishes: Array<{ destination: string; body: string }> = [];

  constructor() {
    this.client = new Client({
      ...ticketedSockJs(CRM_WS_ENDPOINT),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectHeaders: buildStompAuthHeaders()
    });

    this.client.onConnect = () => {
      // Execute delayed subscriptions
      this.delayedSubscriptions.forEach(sub => sub.action());
      this.delayedSubscriptions = [];
      // Flush delayed publishes
      if (this.delayedPublishes.length > 0) {
        this.delayedPublishes.forEach(p => {
          try {
            this.client.publish({ destination: p.destination, body: p.body });
          } catch (err) {
            console.error('Error flushing delayed publish', err);
          }
        });
        this.delayedPublishes = [];
      }
      
      if (this.onConnectCallback) this.onConnectCallback();
    };

    this.client.onStompError = (frame) => {
      console.error('Broker reported error: ' + frame.headers['message']);
      console.error('Additional details: ' + frame.body);
      // Reconectar com a mesma credencial recusada só gera loop de 5s no servidor.
      this.client.deactivate();
      this.delayedPublishes = [];
    };

    this.client.onWebSocketClose = () => {
      if (this.onDisconnectCallback) this.onDisconnectCallback();
    };
  }

  connect(onConnect?: () => void, onDisconnect?: () => void) {
    this.onConnectCallback = onConnect;
    this.onDisconnectCallback = onDisconnect;
    
    // Refresh token on connect
    this.client.connectHeaders = buildStompAuthHeaders();

    // onConnect so dispara no handshake: se ja esta ligado, ninguem mais chama o callback.
    if (this.client.active) {
      if (this.client.connected && onConnect) onConnect();
      return;
    }

    if (!canOpenStompConnection()) {
      console.warn('[STOMP] Sem token válido; ligação adiada.');
      return;
    }

    this.client.activate();
  }

  disconnect() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions.clear();
    this.delayedSubscriptions = [];
    this.client.deactivate();
  }

  get connected(): boolean {
    return this.client.connected;
  }

  ensureConnected() {
    if (this.client.active) return;
    if (!canOpenStompConnection()) return;
    this.client.connectHeaders = buildStompAuthHeaders();
    this.client.activate();
  }

  private safeSubscribe(topic: string, callback: (message: IMessage) => void) {
    if (this.subscriptions.has(topic)) return;

    if (!this.client.connected) {
      if (!this.delayedSubscriptions.some(sub => sub.topic === topic)) {
        this.delayedSubscriptions.push({
          topic,
          action: () => this.safeSubscribe(topic, callback)
        });
      }
      return;
    }

    const sub = this.client.subscribe(topic, callback);
    this.subscriptions.set(topic, sub);
  }

  /** Assinatura crua (corpo em texto) para quem valida o payload por conta própria. */
  subscribe(destination: string, onBody: (body: string) => void) {
    this.safeSubscribe(destination, (message: IMessage) => onBody(message.body));
  }

  subscribeToTopicMessages(topicId: string, callback: (msg: Message) => void) {
    const topic = `/topic/topic/${topicId}`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload: Message = JSON.parse(message.body);
      callback(payload);
    });
  }

  subscribeToMessageUpdated(topicId: string, messageId: string, callback: (msg: Message) => void) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/updated`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload: Message = JSON.parse(message.body);
      callback(payload);
    });
  }

  subscribeToMessageStatus(topicId: string, messageId: string, callback: (msgIdFromTopic: string, status: string) => void) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/status`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const status = message.body.replace(/"/g, '');
      let extractedMsgId = messageId;
      if (messageId === '*' && message.headers.destination) {
        const parts = message.headers.destination.split('/');
        const idx = parts.indexOf('message');
        if (idx !== -1 && idx + 1 < parts.length) {
          extractedMsgId = parts[idx + 1];
        }
      }
      callback(extractedMsgId, status);
    });
  }

  subscribeToMessageDeleted(topicId: string, messageId: string, callback: (messageId: string) => void) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/deleted`;
    this.safeSubscribe(topic, (message: IMessage) => {
      let extractedMsgId = messageId;
      if (messageId === '*' && message.headers.destination) {
        const parts = message.headers.destination.split('/');
        const idx = parts.indexOf('message');
        if (idx !== -1 && idx + 1 < parts.length) {
          extractedMsgId = parts[idx + 1];
        }
      }
      callback(extractedMsgId);
    });
  }

  subscribeToMessagePinned(topicId: string, messageId: string, callback: (messageId: string) => void) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/pinned`;
    this.safeSubscribe(topic, (message: IMessage) => {
      let extractedMsgId = messageId;
      if (messageId === '*' && message.headers.destination) {
        const parts = message.headers.destination.split('/');
        const idx = parts.indexOf('message');
        if (idx !== -1 && idx + 1 < parts.length) {
          extractedMsgId = parts[idx + 1];
        }
      }
      callback(extractedMsgId);
    });
  }

  subscribeToMessageUnpinned(topicId: string, messageId: string, callback: (messageId: string) => void) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/unpinned`;
    this.safeSubscribe(topic, (message: IMessage) => {
      let extractedMsgId = messageId;
      if (messageId === '*' && message.headers.destination) {
        const parts = message.headers.destination.split('/');
        const idx = parts.indexOf('message');
        if (idx !== -1 && idx + 1 < parts.length) {
          extractedMsgId = parts[idx + 1];
        }
      }
      callback(extractedMsgId);
    });
  }

  subscribeToMessageReaction(
    topicId: string,
    messageId: string,
    callback: (messageId: string, emoji: string, userId: string, reactions?: Record<string, string[]>) => void
  ) {
    const topic = `/topic/topic/${topicId}/message/${messageId}/reaction`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload = JSON.parse(message.body) as { messageId: string; emoji: string; userId: number | string; reactions?: Record<string, string[]> };

      let extractedMsgId = payload.messageId || messageId;
      if (messageId === '*' && message.headers.destination) {
        const parts = message.headers.destination.split('/');
        const idx = parts.indexOf('message');
        if (idx !== -1 && idx + 1 < parts.length) {
          extractedMsgId = parts[idx + 1];
        }
      }

      callback(extractedMsgId, payload.emoji, String(payload.userId), payload.reactions);
    });
  }

  subscribeToTyping(topicId: string, callback: (notification: { userId: string; isTyping: boolean }) => void) {
    const topic = `/topic/chat/${topicId}/typing`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload = JSON.parse(message.body);
      callback(payload);
    });
  }

  subscribeToPresence(userId: string, callback: (presence: { userId: string; status: string; lastSeen?: number }) => void) {
    const topic = `/app/presence/${userId}`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload = JSON.parse(message.body);
      callback(payload);
    });
  }

  subscribeToErrors(callback: (errorMsg: string) => void) {
    const topic = `/user/queue/errors`;
    if (this.subscriptions.has(topic)) return;

    const sub = this.client.subscribe(topic, (message: IMessage) => {
      callback(message.body);
    });
    this.subscriptions.set(topic, sub);
  }

  subscribeToWebRTCSignals(callback: (payload: SignalResponse) => void) {
    const topic = `/user/queue/call`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload = JSON.parse(message.body);
      callback(payload);
    });
  }

  // Novo: Inscrição em sala de voz/grupo
  subscribeToRoom(topicId: string, callback: (payload: SignalResponse) => void) {
    const topic = `/topic/topic/${topicId}/room`;
    this.safeSubscribe(topic, (message: IMessage) => {
      const payload = JSON.parse(message.body);
      callback(payload);
    });
  }

  unsubscribe(topic: string) {
    const sub = this.subscriptions.get(topic);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(topic);
    }
  }

  private safePublish(params: { destination: string; body: string; ephemeral?: boolean }) {
    const { destination, body, ephemeral } = params;

    if (!this.client.connected) {
      // Sinais efémeros (heartbeat, typing) perdem sentido ao serem entregues
      // mais tarde: enfileirá-los só acumula lixo enquanto a ligação está em baixo.
      if (ephemeral) return;

      if (!this.delayedPublishes.some(p => p.destination === destination && p.body === body)) {
        this.delayedPublishes.push({ destination, body });
        console.warn(`[STOMP] Cliente desconectado. Enfileirando publicação para ${destination}`);
      }
      return;
    }

    this.client.publish({ destination, body });
  }

  sendMessage(topicId: string, senderId: string, content?: string, type: MessageType = MessageType.TEXT, fileIds?: string[], ref?: MessageRef) {
    const payload = JSON.stringify({
      topicId,
      senderId,
      content,
      type: type === MessageType.TEXT ? 'TEXT' : type,
      fileIds,
      ref,
    });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.send`,
      body: payload,
    });
  }

  sendFileMessage(topicId: string, senderId: string, content: string | undefined, fileIds: string[]) {
    this.sendMessage(topicId, senderId, content, MessageType.FILE, fileIds);
  }

  sendAudioMessage(topicId: string, senderId: string, fileIds: string[]) {
    this.sendMessage(topicId, senderId, undefined, MessageType.AUDIO, fileIds);
  }

  editMessage(topicId: string, messageId: string, content: string) {
    const payload = JSON.stringify({ messageId, content });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.edit`,
      body: payload,
    });
  }

  deleteMessage(topicId: string, messageId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/message.delete`,
      body: JSON.stringify({ messageId }),
    });
  }

  pinMessage(topicId: string, messageId: string) {
    const payload = JSON.stringify({ messageId });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.pin`,
      body: payload,
    });
  }

  replyMessage(topicId: string, senderId: string, content: string, repliedToMessageId: string) {
    const payload = JSON.stringify({ 
      senderId, 
      content, 
      repliedToMessageId 
    });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.reply`,
      body: payload,
    });
  }

  unpinMessage(topicId: string, messageId: string) {
    const payload = JSON.stringify({ messageId });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.unpin`,
      body: payload,
    });
  }

  addReaction(topicId: string, messageId: string, emoji: string, senderId: string) {
    const payload = JSON.stringify({ messageId, emoji, userId: senderId });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.react`,
      body: payload,
    });
  }

  removeReaction(topicId: string, messageId: string, emoji: string, senderId: string) {
    const payload = JSON.stringify({ messageId, emoji, userId: senderId });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.unreact`,
      body: payload,
    });
  }

  forwardMessage(topicId: string, messageId: string, senderId: string) {
    const payload = JSON.stringify({ messageId, senderId });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.forward`,
      body: payload,
    });
  }

  updateMessageStatus(topicId: string, messageId: string, status: string) {
    const payload = JSON.stringify({ messageId, status });
    this.safePublish({
      destination: `/app/topic/${topicId}/message.status`,
      body: payload,
    });
  }

  sendTyping(topicId: string) {
    this.safePublish({
      destination: `/app/chat/${topicId}/typing`,
      body: '',
      ephemeral: true,
    });
  }

  sendPresenceHeartbeat() {
    this.safePublish({
      destination: `/app/presence/heartbeat`,
      body: '',
      ephemeral: true,
    });
  }

  createTopic(workspaceId: string | null | undefined, type: string, members: string[], name?: string) {
    // Normaliza: strings sentinel não são IDs reais de workspace
    const isStandalone = !workspaceId || workspaceId === 'standalone' || workspaceId === 'null';
    const effectiveWorkspaceId = isStandalone ? null : workspaceId;

    const payload = JSON.stringify({ 
      workspaceId: effectiveWorkspaceId, 
      type, 
      members,
      name
    });

    if (!isStandalone) {
      // Tópico vinculado a um workspace específico
      this.safePublish({
        destination: `/app/workspace/${effectiveWorkspaceId}/topic/create`,
        body: payload,
      });
    } else {
      // Tópico standalone / global (sem workspace)
      this.safePublish({
        destination: `/app/topic/create`,
        body: payload,
      });
    }
  }

  addMember(topicId: string, memberId: string) {
    const payload = memberId;
    this.safePublish({
      destination: `/app/topic/${topicId}/addMember`,
      body: payload,
    });
  }

  removeMember(topicId: string, memberId: string) {
    const payload = memberId;
    this.safePublish({
      destination: `/app/topic/${topicId}/removeMember`,
      body: payload,
    });
  }

  updateTopicStatus(topicId: string, status: string) {
    const payload = `"${status}"`; // Spring @Payload StatusTopic expects a plain string representing the Enum
    this.safePublish({
      destination: `/app/topic/${topicId}/updateStatus`,
      body: payload,
    });
  }

  pinTopic(topicId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/pin`,
      body: '',
    });
  }

  unpinTopic(topicId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/unpin`,
      body: '',
    });
  }

  changeMemberRoleToAdmin(topicId: string, memberId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/changeMemberRoleToAdmin`,
      body: memberId,
    });
  }

  changeMemberRoleToViewer(topicId: string, memberId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/changeMemberRoleToViewer`,
      body: memberId,
    });
  }

  changeMemberRoleToDefault(topicId: string, memberId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/changeMemberRoleToDefault`,
      body: memberId,
    });
  }

  joinRoom(topicId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/room.join`,
      body: '',
    });
  }

  leaveRoom(topicId: string) {
    this.safePublish({
      destination: `/app/topic/${topicId}/room.leave`,
      body: '',
    });
  }

  sendWebRTCSignal(targetUserId: string, type: SignalType, payload: object) {
    const body = JSON.stringify({
      targetUserId,
      type,
      payload
    });
    
    this.safePublish({
      destination: `/app/call/signal`,
      body: body,
    });
  }

  createChat(members: string[]) {
    void members;
  }

  updateChatStatus(topicId: string, status: string) {
    this.updateTopicStatus(topicId, status);
  }

  subscribeToChatMessages(topicId: string, callback: (msg: Message) => void) {
    this.subscribeToTopicMessages(topicId, callback);
  }
}

export const chatWebSocket = new ChatWebSocketService();
